import { useNavigate, useSearchParams } from "react-router";
import type { List } from "@guestbook/shared";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/AlertDialog";
import { Button } from "@/shared/components/ui/Button";
import { useDeleteList } from "@/shared/lib/lists";
import { useActiveScope } from "@/shared/lib/scope";

interface DeleteListDialogProps {
  list: List | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Destructive confirmation for deleting a list. Member contacts and
 * households are unaffected — only the list row and its membership entries
 * go away (CASCADE on `list_members.list_id`). If the deleted list is the
 * active scope, clear the `?list=` param so the user lands back on All
 * contacts.
 */
export function DeleteListDialog({ list, open, onOpenChange }: DeleteListDialogProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const remove = useDeleteList();
  const { listId: activeListId } = useActiveScope();

  function handleConfirm() {
    if (!list) return;
    remove.mutate(list.id, {
      onSuccess: () => {
        onOpenChange(false);
        if (activeListId === list.id) {
          if (window.location.pathname === "/app/contacts") {
            const next = new URLSearchParams(searchParams);
            next.delete("list");
            setSearchParams(next);
          } else {
            navigate("/app/contacts");
          }
        }
      },
    });
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this list?</AlertDialogTitle>
          <AlertDialogDescription>
            The list <strong>{list?.name ?? ""}</strong> will be deleted. Contacts
            and households in this list will <strong>not</strong> be deleted.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {remove.error ? (
          <p className="type-body-sm text-danger-strong">{remove.error.message}</p>
        ) : null}
        <AlertDialogFooter>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={remove.isPending}
          >
            {remove.isPending ? "Deleting…" : "Delete list"}
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
